/**
 * Buxgalteriya Loyihasi - Web Server Edition (Modularized)
 */

import React, { useState, useEffect, useRef } from 'react';

// Icons
import {
  LayoutDashboard, Package, ArrowUpCircle, ShoppingCart,
  History, Layers, Edit3, LogOut, Activity, Users, Terminal, Info, X, ChevronRight, Search, HardDrive, AlertCircle, Snowflake,
  TrendingUp, Store, Eye, EyeOff, Skull
} from 'lucide-react';

// Layout Components
import NavItem from './components/Layout/NavItem';
import Toast from './components/Layout/Toast';

// Tab Components
import DashboardTab from './tabs/Dashboard/DashboardTab';
import InventoryTab from './tabs/Inventory/InventoryTab';
import HistoryTab from './tabs/History/HistoryTab';
import AnalyticsTab from './tabs/Analytics/AnalyticsTab';
import IssuesTab from './tabs/Inventory/IssuesTab';
import DeadStockTab from './tabs/Analytics/DeadStockTab';
import ExpiredTab from './tabs/Inventory/ExpiredTab';
import WorkerActivityTab from './tabs/Analytics/WorkerActivityTab';

// Admin Tabs
import AdminDashboard from './tabs/Admin/AdminDashboard';
import UserManagement from './tabs/Admin/UserManagement';
import SystemLogs from './tabs/Admin/SystemLogs';

// Modal Components
import ShopModal from './components/Modals/ShopModal';
import ProductDetailModal from './components/Modals/ProductDetailModal';
import AddProductModal from './components/Modals/AddProductModal';
import GroupDetailModal from './components/Modals/GroupDetailModal';
import ProfileModal from './components/Modals/ProfileModal';

const API_BASE = "/api";

function App() {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user')));
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [activeTab, setActiveTab] = useState(() => {
    const savedUser = JSON.parse(localStorage.getItem('user'));
    if (savedUser?.role === 'admin') return "admin_status";
    return "dashboard";
  });

  // Data States
  const [items, setItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [stats, setStats] = useState({ total_in_stock: 0, total_sold: 0, inventory_value: 0, total_profit: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [activeShopId, setActiveShopId] = useState(null);
  const [inventoryFilter, setInventoryFilter] = useState("all"); // all, verified, unverified
  const [branches, setBranches] = useState([]);
  const [activeBranchId, setActiveBranchId] = useState("all");

  // UI States
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [showLoginPass, setShowLoginPass] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [tin, setTin] = useState(user?.tin || "");
  const [asilToken, setAsilToken] = useState(user?.asil_token || "");
  const [didoxToken, setDidoxToken] = useState(user?.didox_token || "");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [quickSellCode, setQuickSellCode] = useState("");
  const [isBulkSelling, setIsBulkSelling] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showShopModal, setShowShopModal] = useState(false);
  const [shopSearchTerm, setShopSearchTerm] = useState("");
  const [newItem, setNewItem] = useState({ marking_code: "", product_name: "", cost_price: "", unit: "dona" });

  // Admin Specific
  const [adminUsers, setAdminUsers] = useState([]);
  const [systemStatus, setSystemStatus] = useState(null);
  const [serverLogs, setServerLogs] = useState("");
  const [netSpeed, setNetSpeed] = useState({ rx: 0, tx: 0 });
  const [availableShops, setAvailableShops] = useState([]);
  const [newUserInfo, setNewUserInfo] = useState({ email: "", password: "", role: "owner", assignedShops: [] });
  const [editingUserId, setEditingUserId] = useState(null);
  const [showAdminShopModal, setShowAdminShopModal] = useState(false);
  const [adminShopSearch, setAdminShopSearch] = useState("");

  const searchTimeout = useRef(null);
  const prevNetworkRef = useRef(null);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "success" }), 4000);
  };

  // --- API HANDLERS ---
  const fetchBranches = async () => {
    if (!token || user?.role === 'admin') return;
    try {
      const shopParam = activeShopId ? `?shopId=${activeShopId}` : "";
      const res = await fetch(`${API_BASE}/branches${shopParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (Array.isArray(data)) setBranches(data);
    } catch (e) { console.error(e); }
  };

  const fetchData = async (page = currentPage) => {
    if (!token || user?.role === 'admin' || activeTab === 'issues') return;
    setLoading(true);
    const currentOffset = (page - 1) * 50;
    const currentStatus = activeTab === 'inventory' ? 'in_stock' : (activeTab === 'history' ? 'sold' : '');
    const verifiedParam = activeTab === 'inventory' && inventoryFilter !== 'all' ? `&verified=${inventoryFilter === 'verified'}` : "";
    const branchParam = activeBranchId && activeBranchId !== 'all' ? `&branchId=${activeBranchId}` : "";
    try {
      const shopParam = activeShopId ? `&shopId=${activeShopId}` : "";
      // Fetch year/month for dashboard, history OR analytics
      const needsPeriod = activeTab === 'dashboard' || activeTab === 'history' || activeTab === 'analytics';
      const yearParam = needsPeriod ? `&year=${selectedYear}&month=${selectedMonth}` : "";

      const res = await fetch(`${API_BASE}/dashboard?limit=50&offset=${currentOffset}&search=${debouncedSearch}&status=${currentStatus}${shopParam}${yearParam}${verifiedParam}${branchParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.items) setItems(data.items);
      if (data.totalItems !== undefined) setTotalItems(data.totalItems);
      if (data.stats) setStats(data.stats);
      if (data.analytics) setAnalytics(data.analytics);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleLogin = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPass })
      });
      const data = await res.json();
      if (data.token) {
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setActiveTab(data.user.role === 'admin' ? 'admin_status' : 'dashboard');
      } else showToast(data.error || "Xato", "error");
    } catch (e) { showToast("Serverga ulanib bo'lmadi", "error"); }
    setLoading(false);
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const handleAsilSync = async () => {
    if (loading) return;
    setLoading(true);
    showToast("Yangilash boshlandi, iltimos kuting...", "info");
    try {
      const shopParam = activeShopId ? `?shopId=${activeShopId}` : "";
      const res = await fetch(`${API_BASE}/asil/sync${shopParam}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message || "Muvaffaqiyatli!");
        fetchData();
      } else showToast(data.error || "Xato", "error");
    } catch (e) { showToast("Xato yoki Timeout", "error"); }
    setLoading(false);
  };

  const handleDidoxSync = async () => {
    if (loading) return;
    setLoading(true);
    showToast("Didox fakturalari tekshirilmoqda...", "info");
    try {
      const shopParam = activeShopId ? `?shopId=${activeShopId}` : "";
      const res = await fetch(`${API_BASE}/didox/sync${shopParam}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message || "Muvaffaqiyatli!");
        fetchData();
      } else showToast(data.error || "Xato", "error");
    } catch (e) { showToast("Didox bilan bog'lanishda xato", "error"); }
    setLoading(false);
  };

  const handleQuickSell = async (e) => {
    if (e.key === 'Enter' && quickSellCode.trim()) {
      const codes = quickSellCode.split(/[\s,]+/).filter(c => c.trim().length > 10);
      if (codes.length === 0) return;
      setIsBulkSelling(true);
      let successCount = 0;
      for (const code of codes) {
        try {
          const shopParam = activeShopId ? `?shopId=${activeShopId}` : "";
          const res = await fetch(`${API_BASE}/sell${shopParam}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ marking_code: code, sale_price: 0 })
          });
          if (res.ok) successCount++;
        } catch (err) {}
      }
      if (successCount > 0) {
        showToast(`${successCount} ta mahsulot sotildi!`);
        fetchData();
      }
      setQuickSellCode("");
      setIsBulkSelling(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const shopParam = activeShopId ? `?shopId=${activeShopId}` : "";
      const res = await fetch(`${API_BASE}/export-excel${shopParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = "hisobot.xlsx";
        a.click();
      }
    } catch (e) { showToast("Xato", "error"); }
  };

  const handleExportInventory = async () => {
    try {
      const shopParam = activeShopId ? `?shopId=${activeShopId}` : "";
      const res = await fetch(`${API_BASE}/export-inventory${shopParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = "ombor.xlsx";
        a.click();
      }
    } catch (e) { showToast("Xato", "error"); }
  };

  const handleAddProduct = async () => {
    if (!newItem.marking_code || !newItem.product_name || !newItem.cost_price) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/inventory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(newItem)
      });
      if (res.ok) {
        showToast("Qo'shildi!");
        setShowAddModal(false);
        setNewItem({ marking_code: "", product_name: "", cost_price: "", unit: "dona" });
        fetchData();
      }
    } catch (e) { showToast("Xato", "error"); }
    setLoading(false);
  };

  const handleSellOne = async (item) => {
    const price = prompt(`${item.product_name} uchun sotuv narxi:`);
    if (price === null) return;
    try {
      const shopParam = activeShopId ? `?shopId=${activeShopId}` : "";
      const res = await fetch(`${API_BASE}/sell${shopParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ marking_code: item.marking_code, sale_price: Number(price) })
      });
      if (res.ok) {
        showToast("Sotildi!");
        fetchData();
        setSelectedItem(null); setSelectedGroup(null);
      } else showToast("Xato", "error");
    } catch (e) { showToast("Xato", "error"); }
  };

  const handleReturnItem = async (item) => {
    if (!window.confirm("Omborga qaytarilsinmi?")) return;
    try {
      const shopParam = activeShopId ? `?shopId=${activeShopId}` : "";
      const res = await fetch(`${API_BASE}/inventory/return${shopParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ marking_code: item.marking_code })
      });
      if (res.ok) {
        showToast("Qaytarildi");
        fetchData();
        setSelectedItem(null);
      }
    } catch (e) { showToast("Xato", "error"); }
  };

  const handleUpdateProfile = async () => {
    try {
      const shopParam = activeShopId ? `?shopId=${activeShopId}` : "";
      const res = await fetch(`${API_BASE}/profile${shopParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ tin, asil_token: asilToken, didox_token: didoxToken })
      });
      if (res.ok) {
        showToast("Profil yangilandi");
        setShowProfile(false);
      }
    } catch (e) { showToast("Xato", "error"); }
  };

  const handleClearData = async () => {
    if (!window.confirm("DIQQAT! Ushbu do'konning barcha ma'lumotlari (sklad va tarix) o'chiriladi. Tasdiqlaysizmi?")) return;
    setLoading(true);
    try {
      const shopParam = activeShopId ? `?shopId=${activeShopId}` : "";
      const res = await fetch(`${API_BASE}/inventory/clear-shop${shopParam}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        showToast("Ma'lumotlar tozalandi!");
        fetchData();
      } else showToast(data.error || "Xato", "error");
    } catch (e) { showToast("Xato", "error"); }
    setLoading(false);
  };

  // --- ADMIN HANDLERS ---
  const fetchAdminData = async () => {
    if (!token || user?.role !== 'admin') return;
    try {
      if (activeTab === 'admin_users') {
        const res = await fetch(`${API_BASE}/admin/users`, { headers: { 'Authorization': `Bearer ${token}` } });
        setAdminUsers(await res.json());
        const shopRes = await fetch(`${API_BASE}/admin/shops`, { headers: { 'Authorization': `Bearer ${token}` } });
        setAvailableShops(await shopRes.json());
      } else if (activeTab === 'admin_status') {
        const res = await fetch(`${API_BASE}/admin/system-status`, { headers: { 'Authorization': `Bearer ${token}` } });
        const data = await res.json();
        if (data.network && prevNetworkRef.current) {
          setNetSpeed({
            rx: Math.max(0, (data.network.rx - prevNetworkRef.current.rx) / 3 / 1024).toFixed(1),
            tx: Math.max(0, (data.network.tx - prevNetworkRef.current.tx) / 3 / 1024).toFixed(1)
          });
        }
        prevNetworkRef.current = data.network;
        setSystemStatus(data);
      }
    } catch (err) {
      console.error("ADMIN_DATA_FETCH_ERROR:", err);
    }
  };

  const handleCreateUser = async () => {
    const url = editingUserId ? `${API_BASE}/admin/users/${editingUserId}` : `${API_BASE}/admin/users`;
    const method = editingUserId ? 'PUT' : 'POST';
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(newUserInfo)
    });
    if (res.ok) {
      showToast("Bajarildi");
      setNewUserInfo({ email: "", password: "", role: "owner", assignedShops: [] });
      setEditingUserId(null);
      fetchAdminData();
    }
  };

  // --- EFFECTS ---
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(searchTimeout.current);
  }, [searchTerm]);

  useEffect(() => {
    if (token) {
      fetchBranches();
      fetchData();
    }
  }, [token, selectedYear, selectedMonth, debouncedSearch, activeTab, activeShopId, currentPage, inventoryFilter, activeBranchId]);

  useEffect(() => {
    if (token && user?.role === 'admin') {
      fetchAdminData();
      const interval = setInterval(fetchAdminData, activeTab === 'admin_status' ? 3000 : 30000);
      return () => clearInterval(interval);
    }
  }, [token, activeTab]);

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6 font-sans">
        <div className="bg-slate-900 p-12 rounded-[50px] shadow-2xl w-full max-w-md border border-white/5">
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-blue-600 rounded-3xl mx-auto flex items-center justify-center text-white mb-6 shadow-xl font-black text-3xl italic shadow-blue-600/20 animate-pulse">B</div>
            <h1 className="text-2xl font-black text-white">Buxgalteriya Server</h1>
            <p className="text-slate-500 font-bold text-sm mt-2">Tizimga kirish</p>
          </div>
          <div className="space-y-4">
             <input type="email" placeholder="Email" className="w-full p-4 bg-slate-950 border-2 border-transparent rounded-2xl outline-none focus:border-blue-500/20 font-bold text-white transition-all" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} />
             <div className="relative">
                <input
                  type={showLoginPass ? "text" : "password"}
                  placeholder="Parol"
                  className="w-full p-4 bg-slate-950 border-2 border-transparent rounded-2xl outline-none focus:border-blue-500/20 font-bold text-white transition-all"
                  value={loginPass}
                  onChange={e => setLoginPass(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowLoginPass(!showLoginPass)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
                >
                  {showLoginPass ? <EyeOff size={20}/> : <Eye size={20}/>}
                </button>
             </div>
             <button onClick={handleLogin} disabled={loading} className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black hover:bg-blue-700 transition shadow-lg active:scale-95 disabled:opacity-50 uppercase tracking-widest">
               {loading ? "KIRILMOQDA..." : "KIRISH"}
             </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex font-sans text-slate-200">
      <aside className="w-72 bg-slate-900 border-r border-blue-500/10 fixed h-full shadow-[20px_0_50px_rgba(0,0,0,0.5)] z-20 flex flex-col">
        <div className="p-10 flex items-center gap-3 group cursor-default">
          <div className="w-12 h-12 bg-slate-950 border border-blue-500/50 rounded-xl shadow-[0_0_20px_rgba(0,240,255,0.3)] flex items-center justify-center text-blue-400 font-black text-2xl relative overflow-hidden group-hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all duration-500">
            <span className="relative z-10 drop-shadow-[0_0_8px_rgba(0,240,255,0.8)]">B</span>
          </div>
          <div className="flex flex-col">
            <span className="text-xl font-black text-white italic tracking-tighter leading-none">BUXGALTERIYA</span>
            <span className="text-[10px] font-black text-blue-500 tracking-[0.3em] ml-0.5 opacity-80 uppercase">Pro</span>
          </div>
        </div>

        {(user?.role === 'admin' || user?.role === 'accountant') && (
          <div className="px-6 mb-4">
            <button
              onClick={() => setShowShopModal(true)}
              className="w-full flex items-center justify-between p-4 bg-blue-600/10 border border-blue-500/20 rounded-2xl hover:bg-blue-600/20 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-600/20">
                  <Store size={20} />
                </div>
                <div className="text-left">
                  <p className="text-[9px] font-black text-blue-500 uppercase tracking-widest">Hozirgi Do'kon</p>
                  <p className="text-sm font-bold text-white truncate max-w-[120px]">
                    {activeShopId ? (user.assignments?.find(a => a.shop_id === activeShopId)?.shop_email || `ID: ${activeShopId}`) : "TANLANMAGAN"}
                  </p>
                </div>
              </div>
              <ChevronRight size={18} className="text-blue-500 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        )}

        <nav className="p-6 space-y-3 flex-1 overflow-y-auto">
          {user?.role !== 'admin' && (
            <>
              <NavItem icon={<LayoutDashboard/>} label="Dashboard" active={activeTab === 'dashboard'} onClick={() => { setActiveTab('dashboard'); setCurrentPage(1); }} />
              <NavItem icon={<Package/>} label="Ombor Qoldig'i" active={activeTab === 'inventory'} onClick={() => { setActiveTab('inventory'); setCurrentPage(1); }} />
              <NavItem icon={<History/>} label="Savdo Tarixi" active={activeTab === 'history'} onClick={() => { setActiveTab('history'); setCurrentPage(1); }} />
              <NavItem icon={<Activity/>} label="Analitika" active={activeTab === 'analytics'} onClick={() => { setActiveTab('analytics'); setCurrentPage(1); }} />
              <NavItem icon={<Snowflake/>} label="Muzlagan Mablag'" active={activeTab === 'dead_stock'} onClick={() => { setActiveTab('dead_stock'); }} />
              <NavItem icon={<Skull/>} label="Muddati O'tganlar" active={activeTab === 'expired'} onClick={() => { setActiveTab('expired'); }} />
              <NavItem icon={<AlertCircle/>} label="Xatoliklar" active={activeTab === 'issues'} onClick={() => { setActiveTab('issues'); }} />
              <NavItem icon={<Users/>} label="Xodimlarni Kuzatish" active={activeTab === 'worker_activity'} onClick={() => { setActiveTab('worker_activity'); }} />
            </>
          )}
          {user?.role === 'admin' && (
            <div className="pt-6 mt-6 border-t border-white/5 space-y-3">
              <NavItem icon={<Activity/>} label="Server Holati" active={activeTab === 'admin_status'} onClick={() => { setActiveTab('admin_status'); setCurrentPage(1); }} />
              <NavItem icon={<Users/>} label="Foydalanuvchilar" active={activeTab === 'admin_users'} onClick={() => { setActiveTab('admin_users'); setCurrentPage(1); }} />
            </div>
          )}
          {user?.role !== 'admin' && (
             <NavItem icon={<Edit3/>} label="Profil (API)" active={showProfile} onClick={() => setShowProfile(true)} />
          )}
        </nav>
        <div className="p-8 border-t border-white/5">
          <button onClick={handleLogout} className="w-full p-4 bg-rose-500/10 text-rose-500 rounded-2xl font-black hover:bg-rose-500 hover:text-white transition-all flex items-center justify-center gap-2">
            <LogOut size={18}/> CHIQISH
          </button>
        </div>
      </aside>

      <main className="ml-72 flex-1 p-12">
        <header className="flex justify-between items-center mb-12">
          <h2 className="text-4xl font-black text-white tracking-tight capitalize drop-shadow-[0_0_10px_rgba(59,130,246,0.3)]">{activeTab.replace('admin_', ' ').replace('worker_activity', 'Xodimlar Skanerlash Faoliyati')}</h2>
          {user?.role !== 'admin' && (
            <div className="flex gap-4 items-center">
              {/* Branch Selector Dropdown */}
              <div className="flex items-center gap-2 bg-slate-900 border border-blue-500/30 px-5 py-3 rounded-[20px] shadow-lg">
                <Store size={20} className="text-blue-400" />
                <select
                  value={activeBranchId}
                  onChange={(e) => { setActiveBranchId(e.target.value); setCurrentPage(1); }}
                  className="bg-transparent text-white font-black text-sm outline-none cursor-pointer"
                >
                  <option value="all" className="bg-slate-900 text-white">Barcha Filiallar</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id} className="bg-slate-900 text-white">{b.name}</option>
                  ))}
                </select>
              </div>

              <button onClick={handleDidoxSync} disabled={loading} className="bg-blue-600/20 border border-blue-500/30 text-blue-400 px-8 py-3 rounded-[20px] font-black flex items-center gap-3 hover:bg-blue-600 hover:text-white transition">
                <Layers size={22} /> DIDOX SYNC
              </button>
              <button onClick={handleAsilSync} disabled={loading} className="bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 px-8 py-3 rounded-[20px] font-black flex items-center gap-3 hover:bg-emerald-600 hover:text-white transition">
                <Activity size={22} /> ASIL BELGI SYNC
              </button>
            </div>
          )}
        </header>

        {activeTab === 'dashboard' && (
          <DashboardTab
            globalSearch={globalSearch} setGlobalSearch={setGlobalSearch}
            globalResults={items.filter(i => globalSearch.length > 2 && (i.product_name?.toLowerCase().includes(globalSearch.toLowerCase()) || i.marking_code?.toLowerCase().includes(globalSearch.toLowerCase())))}
            setSelectedItem={setSelectedItem} stats={stats} isBulkSelling={isBulkSelling} quickSellCode={quickSellCode} setQuickSellCode={setQuickSellCode}
            handleQuickSell={handleQuickSell}
            selectedYear={selectedYear} selectedMonth={selectedMonth}
            handleClearData={handleClearData}
          />
        )}

        {activeTab === 'inventory' && (
          <InventoryTab
            items={items} totalItems={totalItems} currentPage={currentPage} setCurrentPage={setCurrentPage}
            searchTerm={searchTerm} setSearchTerm={setSearchTerm} handleExportInventory={handleExportInventory}
            setShowAddModal={setShowAddModal} setSelectedGroup={setSelectedGroup}
            inventoryFilter={inventoryFilter} setInventoryFilter={setInventoryFilter}
            stats={stats}
          />
        )}

        {activeTab === 'history' && (
          <HistoryTab
            items={items} totalItems={totalItems} currentPage={currentPage} setCurrentPage={setCurrentPage}
            searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSelectedItem={setSelectedItem}
            selectedYear={selectedYear} setSelectedYear={setSelectedYear}
            selectedMonth={selectedMonth} setSelectedMonth={setSelectedMonth}
          />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsTab
            analytics={analytics} selectedYear={selectedYear} setSelectedYear={setSelectedYear}
            selectedMonth={selectedMonth} setSelectedMonth={setSelectedMonth} handleExportExcel={handleExportExcel}
          />
        )}

        {activeTab === 'issues' && (
          <IssuesTab token={token} activeShopId={activeShopId} setSelectedItem={setSelectedItem} />
        )}

        {activeTab === 'dead_stock' && (
          <DeadStockTab token={token} activeShopId={activeShopId} setSelectedItem={setSelectedItem} />
        )}

        {activeTab === 'expired' && (
          <ExpiredTab token={token} activeShopId={activeShopId} setSelectedItem={setSelectedItem} />
        )}

        {activeTab === 'worker_activity' && (
          <WorkerActivityTab token={token} API_BASE={API_BASE} activeShopId={activeShopId} />
        )}

        {/* --- ADMIN TABS --- */}
        {activeTab === 'admin_status' && (
          <AdminDashboard token={token} status={systemStatus} netSpeed={netSpeed} />
        )}

        {activeTab === 'admin_users' && (
          <UserManagement token={token} showToast={showToast} />
        )}

        {/* --- MODALS --- */}
        <ShopModal show={showShopModal} onClose={() => setShowShopModal(false)} user={user} shopSearchTerm={shopSearchTerm} setShopSearchTerm={setShopSearchTerm} activeShopId={activeShopId} setActiveShopId={setActiveShopId} />
        <ProductDetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          handleSellOne={handleSellOne}
          token={token}
          activeShopId={activeShopId}
          onUpdate={fetchData}
        />
        <AddProductModal show={showAddModal} onClose={() => setShowAddModal(false)} newItem={newItem} setNewItem={setNewItem} handleAddProduct={handleAddProduct} loading={loading} />
        <GroupDetailModal group={selectedGroup} onClose={() => setSelectedGroup(null)} setSelectedItem={setSelectedItem} handleSellOne={handleSellOne} />
        <ProfileModal
          show={showProfile}
          onClose={() => setShowProfile(false)}
          tin={tin}
          setTin={setTin}
          asilToken={asilToken}
          setAsilToken={setAsilToken}
          didoxToken={didoxToken}
          setDidoxToken={setDidoxToken}
          handleUpdateProfile={handleUpdateProfile}
        />

        <Toast toast={toast} onClose={() => setToast({...toast, show: false})} />
      </main>
    </div>
  );
}

function AdminCard({ icon, title, value, color, sub }) {
  const c = { blue: "text-blue-400 border-blue-500/20", emerald: "text-emerald-400 border-emerald-500/20", indigo: "text-indigo-400 border-indigo-500/20", orange: "text-orange-400 border-orange-500/20" };
  return (
    <div className={`bg-slate-900 p-8 rounded-[40px] border ${c[color]} shadow-2xl`}>
      <div className="flex justify-between items-start mb-6">
        <div className="p-4 bg-white/5 rounded-2xl">{React.cloneElement(icon, {size: 32})}</div>
        <span className="text-2xl font-black">{value}</span>
      </div>
      <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest">{title}</p>
      <p className="text-xs font-bold text-slate-400 mt-1">{sub}</p>
    </div>
  );
}

export default App;
